import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import { useOrganizationCreate } from "~/features/admin/organizations/hooks";
import {
  ORGANIZATION_KINDS,
  type OrganizationFormData,
  zOrganizationFormData,
} from "~/features/admin/organizations/types";

interface OrganizationFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

const FORM_ID = "organization-form";

export function OrganizationFormDialog({ open, onOpenChange }: OrganizationFormDialogProps) {
  const { t } = useTranslation();
  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(zOrganizationFormData),
    defaultValues: { name: "", shortCode: "", kind: "business" },
  });
  const { submit, processing } = useOrganizationCreate({
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  // react-hook-form v7 isn't React Compiler-compatible; form.watch() is
  // flagged by the react-hooks/incompatible-library Compiler rule.
  // eslint-disable-next-line react-hooks/incompatible-library
  const kind = form.watch("kind");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{t("feature.organizations.create")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form id={FORM_ID} onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.common.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shortCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.organization.shortCode")}</FormLabel>
                  <FormControl>
                    <Input {...field} className="font-mono" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.organization.kind")}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ORGANIZATION_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`model.organization.kindLabels.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {kind === "consumer" && (
              <p className="flex items-start gap-2 rounded-[var(--radius-md)] border border-info/30 bg-info-bg px-3.5 py-2.5 text-14-regular text-info">
                {t("feature.organizations.consumerNote")}
              </p>
            )}
          </form>
        </Form>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={processing}>
              {t("ui.cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" form={FORM_ID} disabled={processing}>
            {processing && <Loader2 className="animate-spin" />}
            <span>{t("ui.save")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
